import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap, catchError, throwError, of, map, switchMap } from 'rxjs';
import { LoginRequest, LoginResponse, AuthUser } from '../interfaces/auth.interface';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly API_URL = environment.apiUrl; // ใช้ environment config
  private currentUserSubject = new BehaviorSubject<AuthUser | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    console.log('🚀 AuthService: Constructor called');
    console.log('🌐 API URL:', this.API_URL);
    // Check if user is already logged in on app initialization
    this.checkStoredUser();

    // Debug: Log current user emissions
    this.currentUser$.subscribe(user => {
      console.log('🔔 AuthService: currentUser$ emitted:', user ? user.cid : 'null');
    });
  }

  login(loginData: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.API_URL}/users/login`, loginData).pipe(
      switchMap(response => {
        const authUser: AuthUser = {
          id: response.id,
          email: response.email,
          cid: response.cid,
          isActive: response.isActive,
          forcePasswordChange: response.forcePasswordChange,
          userType: this.determineUserType(loginData),
          roles: response.userRoles?.map(ur => ur.role.name) || []
        };

        this.setCurrentUser(authUser);

        // If force password change is required, redirect immediately
        if (authUser.forcePasswordChange) {
          console.log('🔐 forcePasswordChange=true — redirecting to /force-change-password');
          this.router.navigate(['/force-change-password']);
          return [response];
        }

        // Check if user information exists and redirect accordingly
        return this.checkUserInformation(authUser.id).pipe(
          tap(hasUserInfo => {
            console.log('🔍 User info check result:', hasUserInfo);
            this.redirectAfterLogin(authUser.userType, hasUserInfo);
          }),
          map(() => response)
        );
      }),
      catchError(error => {
        console.error('Login error:', error);
        return throwError(() => error);
      })
    );
  }

  staffLogin(loginData: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.API_URL}/staff/login`, loginData).pipe(
      tap(response => {
        const authUser: AuthUser = {
          id: response.id,
          email: response.email,
          cid: response.cid,
          isActive: response.isActive,
          forcePasswordChange: response.forcePasswordChange,
          userType: 'staff',
          roles: response.userRoles?.map(ur => ur.role.name) || []
        };

        this.setCurrentUser(authUser);

        // If force password change is required, redirect immediately
        if (authUser.forcePasswordChange) {
          console.log('🔐 forcePasswordChange=true — redirecting to /force-change-password');
          this.router.navigate(['/force-change-password']);
          return;
        }

        // Staff users always go to staff dashboard (no user-information check needed)
        this.redirectAfterLogin(authUser.userType, true);
      }),
      catchError(error => {
        console.error('Staff login error:', error);
        return throwError(() => error);
      })
    );
  }

  logout(): void {
    localStorage.removeItem('currentUser');
    this.currentUserSubject.next(null);
    this.router.navigate(['/login']);
  }

  isAuthenticated(): boolean {
    return this.currentUserSubject.value !== null;
  }

  getCurrentUser(): AuthUser | null {
    return this.currentUserSubject.value;
  }

  isStaff(): boolean {
    const user = this.getCurrentUser();
    return user?.userType === 'staff';
  }

  isUser(): boolean {
    const user = this.getCurrentUser();
    return user?.userType === 'user';
  }

  private checkStoredUser(): void {
    console.log('🔍 AuthService: Checking stored user in localStorage...');
    const stored = localStorage.getItem('currentUser');
    if (stored) {
      try {
        const user = JSON.parse(stored) as AuthUser;
        console.log('✅ AuthService: Found stored user:', user);
        this.currentUserSubject.next(user);
        console.log('📤 AuthService: Emitted stored user to currentUser$');
      } catch (error) {
        console.error('❌ AuthService: Error parsing stored user:', error);
        localStorage.removeItem('currentUser');
        this.currentUserSubject.next(null);
      }
    } else {
      console.log('⚠️ AuthService: No stored user found in localStorage');
      this.currentUserSubject.next(null);
    }
  }

  private setCurrentUser(user: AuthUser): void {
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  /**
   * Update current user data (public method for components to use)
   */
  updateCurrentUser(user: AuthUser): void {
    this.setCurrentUser(user);
  }

  private determineUserType(loginData: LoginRequest): 'staff' | 'user' {
    // If login was done with email, it's a staff member
    // If login was done with CID, it's a regular user
    return loginData.email ? 'staff' : 'user';
  }

  /**
   * Check if user information exists for the given user ID
   */
  private checkUserInformation(userId: string): Observable<boolean> {
    return this.http.get(`${this.API_URL}/user-information/user/${userId}`).pipe(
      tap(() => console.log('✅ User information exists')),
      map(() => true),
      catchError(error => {
        if (error.status === 404) {
          console.log('⚠️ User information not found (404)');
          return of(false);
        }
        console.error('❌ Error checking user information:', error);
        // For other errors, assume user info doesn't exist
        return of(false);
      })
    );
  }

  private redirectAfterLogin(userType: 'staff' | 'user', hasUserInfo: boolean = true): void {
    console.log('🔄 redirectAfterLogin called - userType:', userType, 'hasUserInfo:', hasUserInfo);

    if (userType === 'staff') {
      console.log('👔 Staff user - navigating to /staff/dashboard');
      this.router.navigate(['/staff/dashboard']);
    } else {
      // For regular users, check if user information exists
      if (hasUserInfo) {
        console.log('✅ User has info - navigating to /user/dashboard');
        this.router.navigate(['/user/dashboard']);
      } else {
        // Redirect to register page if user information doesn't exist
        console.log('⚠️ User has NO info - navigating to /register');

        // Use setTimeout to ensure navigation happens after login completes
        setTimeout(() => {
          console.log('🚀 Executing navigation to /register from auth service');
          this.router.navigate(['/register']).then(
            (success) => console.log('✅ Auth service navigation successful:', success),
            (error) => console.error('❌ Auth service navigation failed:', error)
          );
        }, 100);
      }
    }
  }
} 